import { useState } from 'react';
import { useFocusTrap } from '@/hooks/useFocusTrap';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plug, Plus, Trash2, CheckCircle2, XCircle, RefreshCw,
  Server, Cloud, HardDrive, FolderOpen, ChevronRight, File, Folder,
} from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader } from '@/components/shared/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAuthStore } from '@/stores/authStore';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import type { TenantConnector, ConnectorFileEntry } from '@/types/api';

const TYPE_META: Record<string, { icon: typeof Server; label: string; color: string }> = {
  sftp: { icon: Server, label: 'SFTP', color: 'text-teal' },
  sharepoint: { icon: Cloud, label: 'SharePoint', color: 'text-blue-500' },
  googledrive: { icon: Cloud, label: 'Google Drive', color: 'text-green-500' },
  confluence: { icon: HardDrive, label: 'Confluence', color: 'text-purple-500' },
};

export function ConnectorsPage() {
  const user = useAuthStore((s) => s.user);
  const queryClient = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [browsingId, setBrowsingId] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['connectors'],
    queryFn: () => api.connectors.list(),
    enabled: !!user,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.connectors.remove(id),
    onSuccess: () => {
      toast.success('Connector removed');
      queryClient.invalidateQueries({ queryKey: ['connectors'] });
    },
    onError: () => toast.error('Failed to remove connector'),
  });

  const testMutation = useMutation({
    mutationFn: (id: string) => api.connectors.test(id),
    onSuccess: (result) => {
      if (result.healthy) {
        toast.success(`Connection healthy (${result.latencyMs}ms)`);
      } else {
        toast.error(`Connection failed: ${result.error}`);
      }
      queryClient.invalidateQueries({ queryKey: ['connectors'] });
    },
    onError: () => toast.error('Test failed'),
  });

  const connectors = data?.connectors ?? [];

  return (
    <div className="flex h-full flex-col gap-6 overflow-y-auto p-6">
      <PageHeader
        title="Connectors"
        subtitle="Configure document write-back targets"
        actions={
          <Button size="sm" onClick={() => setShowCreate(true)}>
            <Plus className="mr-1.5 h-3.5 w-3.5" />
            Add Connector
          </Button>
        }
      />

      {showCreate && (
        <CreateConnectorForm
          onCreated={() => {
            setShowCreate(false);
            queryClient.invalidateQueries({ queryKey: ['connectors'] });
          }}
          onCancel={() => setShowCreate(false)}
        />
      )}

      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 3 }, (_, i) => (
            <div key={i} className="h-20 animate-pulse rounded-lg bg-card" />
          ))}
        </div>
      ) : connectors.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Plug className="h-10 w-10 text-muted-foreground/40" />
            <p className="mt-3 text-sm font-medium">No connectors configured</p>
            <p className="mt-1 text-xs text-muted-foreground">
              Add a connector to enable document write-back to external systems.
            </p>
            <Button size="sm" className="mt-3" onClick={() => setShowCreate(true)}>
              <Plus className="mr-1 h-3.5 w-3.5" /> Add Connector
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {connectors.map((conn) => (
            <ConnectorCard
              key={conn.connectorId}
              connector={conn}
              onTest={() => testMutation.mutate(conn.connectorId)}
              onDelete={() => deleteMutation.mutate(conn.connectorId)}
              onBrowse={() => setBrowsingId(browsingId === conn.connectorId ? null : conn.connectorId)}
              isTesting={testMutation.isPending}
            />
          ))}
        </div>
      )}

      {browsingId && (
        <FileBrowser connectorId={browsingId} onClose={() => setBrowsingId(null)} />
      )}
    </div>
  );
}

function ConnectorCard({
  connector,
  onTest,
  onDelete,
  onBrowse,
  isTesting,
}: {
  readonly connector: TenantConnector;
  readonly onTest: () => void;
  readonly onDelete: () => void;
  readonly onBrowse: () => void;
  readonly isTesting: boolean;
}) {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const meta = TYPE_META[connector.type] ?? TYPE_META.sftp;
  const Icon = meta.icon;

  return (
    <Card>
      <CardContent className="flex items-center gap-4 p-4">
        <div className={cn('shrink-0', meta.color)}>
          <Icon className="h-5 w-5" />
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="text-sm font-medium text-foreground">{connector.name}</p>
            <Badge variant="outline" className="text-[10px]">{meta.label}</Badge>
            {connector.lastTestStatus === 'healthy' && (
              <CheckCircle2 className="h-3.5 w-3.5 text-compliance-low" />
            )}
            {connector.lastTestStatus === 'failed' && (
              <XCircle className="h-3.5 w-3.5 text-compliance-critical" />
            )}
          </div>
          <div className="mt-0.5 flex items-center gap-3 text-[10px] text-muted-foreground">
            {Boolean(connector.config.host) && <span>Host: {connector.config.host as string}</span>}
            {Boolean(connector.config.basePath) && <span>Path: {connector.config.basePath as string}</span>}
            {connector.lastTestedAt && (
              <span>Tested: {new Date(connector.lastTestedAt).toLocaleString()}</span>
            )}
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-1.5">
          <Button variant="ghost" size="sm" className="h-auto p-1.5 text-xs" onClick={onBrowse}>
            <FolderOpen className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="outline" size="sm"
            className="h-auto p-1.5 text-xs"
            onClick={onTest}
            disabled={isTesting}
          >
            <RefreshCw className={cn('h-3.5 w-3.5', isTesting && 'animate-spin')} />
          </Button>
          {showDeleteConfirm ? (
            <div className="flex items-center gap-1 rounded border border-compliance-critical/30 bg-compliance-critical/5 px-2 py-1">
              <Button
                variant="outline" size="sm"
                className="h-5 px-2 text-[10px] border-compliance-critical/50 text-compliance-critical hover:bg-compliance-critical hover:text-white"
                onClick={() => { onDelete(); setShowDeleteConfirm(false); }}
              >
                Yes
              </Button>
              <Button variant="ghost" size="sm" className="h-5 px-2 text-[10px]" onClick={() => setShowDeleteConfirm(false)}>
                No
              </Button>
            </div>
          ) : (
            <Button
              variant="ghost" size="sm"
              className="h-auto p-1.5 text-muted-foreground hover:text-compliance-critical"
              onClick={() => setShowDeleteConfirm(true)}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function CreateConnectorForm({
  onCreated,
  onCancel,
}: {
  readonly onCreated: () => void;
  readonly onCancel: () => void;
}) {
  const formRef = useFocusTrap(true, onCancel);
  const [name, setName] = useState('');
  const [type, setType] = useState('sftp');
  const [host, setHost] = useState('');
  const [port, setPort] = useState('22');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [basePath, setBasePath] = useState('/');

  const createMutation = useMutation({
    mutationFn: (input: { name: string; type: string; config: Record<string, unknown> }) =>
      api.connectors.create(input),
    onSuccess: () => {
      toast.success('Connector created');
      onCreated();
    },
    onError: () => toast.error('Failed to create connector'),
  });

  const handleSubmit = () => {
    if (!name.trim() || !host.trim() || !username.trim()) {
      toast.error('Name, host, and username are required');
      return;
    }

    const config: Record<string, unknown> = { host, username, basePath };
    if (type === 'sftp') {
      config.port = parseInt(port, 10);
      if (password) config.password = password;
    }
    if (type === 'sharepoint') {
      config.clientId = username;
      config.clientSecret = password;
      config.siteId = basePath;
    }

    createMutation.mutate({ name: name.trim(), type, config });
  };

  return (
    <div ref={formRef} role="dialog" aria-label="Create new connector">
    <Card className="border-teal/30">
      <CardContent className="p-4">
        <h3 className="mb-3 text-sm font-semibold">New Connector</h3>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <div>
            <label className="text-xs font-medium text-muted-foreground">Name</label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g., Policy Drive" className="mt-1" />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Type</label>
            <Select value={type} onValueChange={(v) => setType(v ?? 'sftp')}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="sftp">SFTP</SelectItem>
                <SelectItem value="sharepoint">SharePoint</SelectItem>
                <SelectItem value="googledrive">Google Drive</SelectItem>
                <SelectItem value="confluence">Confluence</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Host</label>
            <Input value={host} onChange={(e) => setHost(e.target.value)} placeholder="sftp.example.com" className="mt-1" />
          </div>
          {type === 'sftp' && (
            <div>
              <label className="text-xs font-medium text-muted-foreground">Port</label>
              <Input type="number" value={port} onChange={(e) => setPort(e.target.value)} className="mt-1" />
            </div>
          )}
          <div>
            <label className="text-xs font-medium text-muted-foreground">
              {type === 'sharepoint' ? 'Client ID' : 'Username'}
            </label>
            <Input value={username} onChange={(e) => setUsername(e.target.value)} className="mt-1" />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">
              {type === 'sharepoint' ? 'Client Secret' : 'Password'}
            </label>
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1" />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">
              {type === 'sharepoint' ? 'Site ID' : 'Base Path'}
            </label>
            <Input value={basePath} onChange={(e) => setBasePath(e.target.value)} className="mt-1" />
          </div>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onCancel}>Cancel</Button>
          <Button size="sm" onClick={handleSubmit} disabled={createMutation.isPending}>
            {createMutation.isPending ? 'Creating...' : 'Create Connector'}
          </Button>
        </div>
      </CardContent>
    </Card>
    </div>
  );
}

function FileBrowser({
  connectorId,
  onClose,
}: {
  readonly connectorId: string;
  readonly onClose: () => void;
}) {
  const [currentPath, setCurrentPath] = useState('/');

  const { data, isLoading } = useQuery({
    queryKey: ['connector-browse', connectorId, currentPath],
    queryFn: () => api.connectors.browse(connectorId, currentPath),
  });

  const files = data?.files ?? [];
  const pathParts = currentPath.split('/').filter(Boolean);

  const navigateTo = (entry: ConnectorFileEntry) => {
    if (entry.type === 'directory') {
      setCurrentPath(entry.path);
    }
  };

  const navigateUp = () => {
    const parts = currentPath.split('/').filter(Boolean);
    parts.pop();
    setCurrentPath('/' + parts.join('/'));
  };

  return (
    <Card className="border-teal/30">
      <CardContent className="p-4">
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-1 text-sm">
            <button className="text-teal hover:underline" onClick={() => setCurrentPath('/')}>/</button>
            {pathParts.map((part, idx) => (
              <span key={idx} className="flex items-center gap-1">
                <ChevronRight className="h-3 w-3 text-muted-foreground" />
                <button
                  className="text-teal hover:underline"
                  onClick={() => setCurrentPath('/' + pathParts.slice(0, idx + 1).join('/'))}
                >
                  {part}
                </button>
              </span>
            ))}
          </div>
          <Button variant="ghost" size="sm" className="text-xs" onClick={onClose}>Close</Button>
        </div>

        {isLoading ? (
          <div className="py-8 text-center text-sm text-muted-foreground">Loading...</div>
        ) : files.length === 0 ? (
          <div className="py-8 text-center text-sm text-muted-foreground">Empty directory</div>
        ) : (
          <div className="max-h-64 space-y-0.5 overflow-y-auto rounded border">
            {currentPath !== '/' && (
              <button
                className="flex w-full items-center gap-2 px-3 py-1.5 text-sm hover:bg-muted/50"
                onClick={navigateUp}
              >
                <Folder className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="text-muted-foreground">..</span>
              </button>
            )}
            {files.map((entry) => (
              <button
                key={entry.path}
                className="flex w-full items-center gap-2 px-3 py-1.5 text-sm hover:bg-muted/50"
                onClick={() => navigateTo(entry)}
              >
                {entry.type === 'directory' ? (
                  <Folder className="h-3.5 w-3.5 text-teal" />
                ) : (
                  <File className="h-3.5 w-3.5 text-muted-foreground" />
                )}
                <span className="flex-1 text-left">{entry.name}</span>
                <span className="text-[10px] text-muted-foreground">
                  {entry.type === 'file' ? `${(entry.size / 1024).toFixed(1)}KB` : ''}
                </span>
              </button>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
